import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '../app.model';
import { apiConstant } from '../app.constant';
import { environment } from 'src/environments/environment';
import { map, tap } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private userSubject: BehaviorSubject<User>;
  public user: Observable<User>;

  private impersonatingSubject: BehaviorSubject<boolean>;
  public isImpersonating$: Observable<boolean>;
  public impersonatingAgencyName: string | null = null;

  constructor(private http: HttpClient, private router: Router) {

    this.userSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('user') || '{}'));
    this.user = this.userSubject.asObservable();

    const isImpersonating = !!localStorage.getItem('impersonatingAgency');
    this.impersonatingSubject = new BehaviorSubject<boolean>(isImpersonating);
    this.isImpersonating$ = this.impersonatingSubject.asObservable();
    this.impersonatingAgencyName = localStorage.getItem('impersonatingAgencyName');
  }

  public get userValue(): User {
    return this.userSubject.value;
  }

  public get isImpersonating(): boolean {
    return this.impersonatingSubject.value;
  }

  onGoogleLogin(access_token: string){
    return this.http.post<User>(`${environment.baseUrl}` + apiConstant.VERIFY_GOOGLE_TOKEN, { access_token })
      .pipe(map(user => {
        localStorage.setItem('user', JSON.stringify(user));
        this.userSubject.next(user);
        return user;
      })); 
  }

  getInitials(name: string){
    if (!name) return '';
    const firstName = name.split(' ')[0]
    const secondName = name.split(' ')[1]??''
    return firstName.substring(0, 1)+secondName.substring(0, 1)
  }

  login(email: string, password: string) {
    return this.http.post<User>(`${environment.baseUrl}` + apiConstant.LOGIN, { email, password })
      .pipe(map(user => {
        localStorage.setItem('user', JSON.stringify(user));
        this.userSubject.next(user);
        return user;
      })); 
  }

  logout() {
    localStorage.removeItem('user');
    localStorage.removeItem('agency_uuid');
    localStorage.removeItem('permissions');
    localStorage.removeItem('profile_pic');
    localStorage.removeItem('dateFormat');
    localStorage.removeItem('candidateTrackingHrs');
    localStorage.removeItem('timesheetSubmissionEmails');
    localStorage.removeItem('timesheetApprovalEmails');
    localStorage.removeItem('impersonatingAgency');
    localStorage.removeItem('impersonatingAgencyName');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('originalPermissions');
    this.impersonatingSubject.next(false);
    this.impersonatingAgencyName = null;
    this.router.navigate(['login']);
    this.http.post<User>(`${environment.baseUrl}` + apiConstant.LOGOUT, {}).subscribe(res => {
      console.log(res);
    })
  }

  startImpersonation(agencyUuid: string) {
    return this.http.post<any>(`${environment.baseUrl}` + apiConstant.IMPERSONATE + '/' + agencyUuid, {})
      .pipe(tap(res => {
        // Save original Super Admin state
        localStorage.setItem('originalUser', localStorage.getItem('user') || '{}');
        localStorage.setItem('originalPermissions', localStorage.getItem('permissions') || '[]');

        // Update user with impersonated agency context
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        currentUser.role_uuid = res.role_uuid;
        currentUser.agency_uuid = res.agency_uuid;
        localStorage.setItem('user', JSON.stringify(currentUser));
        localStorage.setItem('permissions', JSON.stringify(res.permissions));
        localStorage.setItem('impersonatingAgency', res.agency_uuid);
        localStorage.setItem('impersonatingAgencyName', res.agency_name);

        this.impersonatingAgencyName = res.agency_name;
        this.userSubject.next(currentUser);
        this.impersonatingSubject.next(true);
      }));
  }

  stopImpersonation() {
    return this.http.post<any>(`${environment.baseUrl}` + apiConstant.STOP_IMPERSONATE, {})
      .pipe(tap(res => {
        // Restore original Super Admin state
        const originalUser = localStorage.getItem('originalUser');
        if (originalUser) {
          localStorage.setItem('user', originalUser);
          this.userSubject.next(JSON.parse(originalUser));
        }
        const originalPermissions = localStorage.getItem('originalPermissions');
        if (originalPermissions) {
          localStorage.setItem('permissions', originalPermissions);
        }

        // Clear impersonation state
        localStorage.removeItem('impersonatingAgency');
        localStorage.removeItem('impersonatingAgencyName');
        localStorage.removeItem('originalUser');
        localStorage.removeItem('originalPermissions');

        this.impersonatingAgencyName = null;
        this.impersonatingSubject.next(false);
      }));
  }
}
